import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Typography,
    Checkbox,
    FormControlLabel,
    Button,
    Paper
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";

export default function Splines({ onSplineChange }) {
    const [g2Same, setG2Same] = useState(true);

    const [g1NodeCount, setG1NodeCount] = useState(10);
    const [g2NodeCount, setG2NodeCount] = useState(10);

    const [g1Text, setG1Text] = useState("");
    const [g2Text, setG2Text] = useState("");

    const [g1Spline, setG1Spline] = useState({
        sSplineNodeCount: 10,
        afX: [],
        afY: [],
        afY2: []
    });

    const [g2Spline, setG2Spline] = useState({
        sSplineNodeCount: 10,
        afX: [],
        afY: [],
        afY2: []
    });
    const parseSplineText = (text) => {
        const rows = text
            .split(/\r?\n/)
            .map(r => r.trim())
            .filter(Boolean);

        if (rows.length < 3) {
            return {
                afX: [],
                afY: [],
                afY2: []
            };
        }

        const afX = rows[0]
            .split(/[,\t ]+/)
            .map(Number)
            .filter(v => !isNaN(v));

        const afY = rows[1]
            .split(/[,\t ]+/)
            .map(Number)
            .filter(v => !isNaN(v));

        const afY2 = rows[2]
            .split(/[,\t ]+/)
            .map(Number)
            .filter(v => !isNaN(v));

        return {
            afX,
            afY,
            afY2
        };
    };
   
    const handleFileUpload = (file, setter) => {
        if (!file) return;

        const reader = new FileReader();

        reader.onload = e => {
            setter(e.target.result);
        };

        reader.readAsText(file);
    };

    const createArray = (count) =>
        Array.from({ length: count }, () => 0);

    const [g1X, setG1X] = useState(createArray(10));
    const [g1Y, setG1Y] = useState(createArray(10));
    const [g1Y2, setG1Y2] = useState(createArray(10));

    const [g2X, setG2X] = useState(createArray(10));
    const [g2Y, setG2Y] = useState(createArray(10));
    const [g2Y2, setG2Y2] = useState(createArray(10));
    const resizeArray = (arr, count) => {
        const newArr = [...arr];

        while (newArr.length < count)
            newArr.push(0);

        return newArr.slice(0, count);
    };

    const handleG1NodeCountChange = (value) => {
        const count = Math.max(
            1,
            Number(value || 1)
        );

        setG1NodeCount(count);

        setG1X(prev => resizeArray(prev, count));
        setG1Y(prev => resizeArray(prev, count));
        setG1Y2(prev => resizeArray(prev, count));
    };

    const handleG2NodeCountChange = (value) => {
        const count = Math.max(
            1,
            Number(value || 1)
        );

        setG2NodeCount(count);

        setG2X(prev => resizeArray(prev, count));
        setG2Y(prev => resizeArray(prev, count));
        setG2Y2(prev => resizeArray(prev, count));
    };
    const SplineRow = ({
        label,
        values,
        setValues,
        visibleCount
    }) => (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1.5,
                flexWrap: "nowrap",
                overflowX: "auto"
            }}
        >
            <Typography
                sx={{
                    width: 70,
                    minWidth: 70,
                    fontWeight: 600
                }}
            >
                {label}
            </Typography>

            {values
                .map((v, idx) => (
                    <TextField
                        key={idx}
                        size="small"
                        type="number"
                        value={v}
                        onChange={(e) => {
                            const updated = [...values];
                            updated[idx] = e.target.value;
                            setValues(updated);
                        }}
                        sx={{
                            width: 80,
                            minWidth: 80
                        }}
                    />
                ))}
        </Box>
    );
    const handleG1Parse = () => {
        const result = parseSplineText(g1Text);

        console.log("PARSED:", result);

        const count = result.afX.length;

        setG1NodeCount(count);

        setG1X([...result.afX]);
        setG1Y([...result.afY]);
        setG1Y2([...result.afY2]);

        setG1Spline({
            sSplineNodeCount: count,
            afX: [...result.afX],
            afY: [...result.afY],
            afY2: [...result.afY2]
        });
    };
    const handleG2Parse = () => {
        const result = parseSplineText(g2Text);

        console.log("PARSED:", result);

        const count = result.afX.length;

        setG2NodeCount(count);

        setG2X([...result.afX]);
        setG2Y([...result.afY]);
        setG2Y2([...result.afY2]);

        setG2Spline({
            sSplineNodeCount: count,
            afX: [...result.afX],
            afY: [...result.afY],
            afY2: [...result.afY2]
        });
    };
    useEffect(() => {
        const g1 = {
            sSplineNodeCount: Number(g1NodeCount),
            afX: g1X.map(Number),
            afY: g1Y.map(Number),
            afY2: g1Y2.map(Number)
        };

        const g2 = g2Same
            ? g1
            : {
                sSplineNodeCount: Number(g2NodeCount),
                afX: g2X.map(Number),
                afY: g2Y.map(Number),
                afY2: g2Y2.map(Number)
            };

        onSplineChange?.({
            g2Same,
            xG1Spline: g1,
            xG2Spline: g2
        });
    }, [
        g2Same,
        g1NodeCount,
        g2NodeCount,
        g1X,
        g1Y,
        g1Y2,
        g2X,
        g2Y,
        g2Y2
    ]);
    return (
        <Paper
            elevation={2}
            sx={{
                p: 2,
                mt: 2,
                borderRadius: 2
            }}
        >
            <Typography
                variant="h6"
                gutterBottom
            >
                Spline Inputs
            </Typography>

            <FormControlLabel
                control={
                    <Checkbox
                        checked={g2Same}
                        onChange={e =>
                            setG2Same(
                                e.target.checked
                            )
                        }
                    />
                }
                label="G1 and G2 the same?"
            />

            <Box sx={{ mt: 2 }}>
                <TextField
                    label="G1 Spline Node Count"
                    type="number"
                    size="small"
                    value={g1NodeCount}
                    onChange={(e) =>
                        handleG1NodeCountChange(
                            e.target.value
                        )
                    }
                    sx={{ width: 220 }}
                />
            </Box>

            <Box sx={{ mt: 2 }}>
                <SplineRow
                    label="G1 X"
                    values={g1X}
                    setValues={setG1X}
                    visibleCount={Number(g1NodeCount)}
                />

                <SplineRow
                    label="G1 Y"
                    values={g1Y}
                    setValues={setG1Y}
                    visibleCount={Number(g1NodeCount)}
                />

                <SplineRow
                    label="G1 Y2"
                    values={g1Y2}
                    setValues={setG1Y2}
                    visibleCount={Number(g1NodeCount)}
                />
            </Box>
            {/* G1 */}
            <Box sx={{ mt: 3 }}>
                <Typography
                    variant="subtitle1"
                >
                    G1 Parse Box
                </Typography>

                <TextField
                    multiline
                    rows={8}
                    fullWidth
                    value={g1Text}
                    onChange={e =>
                        setG1Text(
                            e.target.value
                        )
                    }
                    placeholder="X Y Y2"
                />

                <Box
                    sx={{
                        mt: 1,
                        display: "flex",
                        gap: 1
                    }}
                >
                    <Button
                        component="label"
                        variant="outlined"
                        startIcon={
                            <UploadFileIcon />
                        }
                    >
                        Upload
                        <input
                            hidden
                            type="file"
                            accept=".txt,.csv"
                            onChange={e =>
                                handleFileUpload(
                                    e.target.files[0],
                                    setG1Text
                                )
                            }
                        />
                    </Button>

                    <Button
                        variant="contained"
                        onClick={
                            handleG1Parse
                        }
                    >
                        Parse
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={() => {
                            setG1Text("");
                            setG1Spline({
                                sSplineNodeCount: 0,
                                afX: [],
                                afY: [],
                                afY2: []
                            });
                        }}
                    >
                        Clear
                    </Button>
                </Box>
            </Box>

            {/* G2 */}
            {!g2Same && (
                <>
                    <Box sx={{ mt: 3 }}>
                        <TextField
                            label="G2 Spline Node Count"
                            type="number"
                            size="small"
                            value={g2NodeCount}
                            onChange={(e) =>
                                handleG2NodeCountChange(
                                    e.target.value
                                )
                            }
                            sx={{ width: 220 }}
                        />
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <SplineRow
                            label="G2 X"
                            values={g2X}
                            setValues={setG2X}
                            visibleCount={Number(g2NodeCount)}
                        />

                        <SplineRow
                            label="G2 Y"
                            values={g2Y}
                            setValues={setG2Y}
                            visibleCount={Number(g2NodeCount)}
                        />

                        <SplineRow
                            label="G2 Y2"
                            values={g2Y2}
                            setValues={setG2Y2}
                            visibleCount={Number(g2NodeCount)}
                        />
                    </Box>

                    {/* G2 */}
                    <Box sx={{ mt: 3 }}>
                        <Typography
                            variant="subtitle1"
                        >
                            G2 Parse Box
                        </Typography>

                        <TextField
                            multiline
                            rows={8}
                            fullWidth
                            value={g2Text}
                            onChange={e =>
                                setG2Text(
                                    e.target.value
                                )
                            }
                            placeholder="X Y Y2"
                        />

                        <Box
                            sx={{
                                mt: 1,
                                display: "flex",
                                gap: 1
                            }}
                        >
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={
                                    <UploadFileIcon />
                                }
                            >
                                Upload
                                <input
                                    hidden
                                    type="file"
                                    accept=".txt,.csv"
                                    onChange={e =>
                                        handleFileUpload(
                                            e.target.files[0],
                                            setG2Text
                                        )
                                    }
                                />
                            </Button>

                            <Button
                                variant="contained"
                                onClick={
                                    handleG2Parse
                                }
                            >
                                Parse
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setG2Text("");
                                    setG2Spline({
                                        sSplineNodeCount: 0,
                                        afX: [],
                                        afY: [],
                                        afY2: []
                                    });
                                }}
                            >
                                Clear
                            </Button>
                        </Box>
                    </Box>
                </>
            )}
        </Paper>
    );
}